import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ethers } from 'ethers'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { uploadMarketplaceMetadata } from './marketplaceData'
import { AdminTab } from './MarketplaceAdmin'
import { buildVisibleFeeRows } from './marketplaceAdminModel'
import { buildFeeBreakdown, PAGE_SIZE, shortAddress } from './marketplaceModel'
import { marketplaceText } from './marketplaceCopy'
import { useMarketplace } from './useMarketplace'
import {
    AddressValue,
    canAccessMarketAdmin,
    EmptyState,
    formatAmount,
    formatDate,
    formatRemaining,
    MarketDialog,
    MarketSelect,
    MarketplaceHeader,
    NftCard,
    NftDescription,
    NftMedia,
    Pagination,
    ProjectLogo,
    Toast,
    TransactionSuccessDialog,
} from './MarketplaceParts'
import './marketplace.css'

const ZERO_ADDRESS = ethers.constants.AddressZero
const PROJECT_LINKS = [
    { key: 'website', icon: 'fa fa-globe' },
    { key: 'twitter', icon: 'fab fa-twitter' },
    { key: 'telegram', icon: 'fab fa-telegram-plane' },
    { key: 'youtube', icon: 'fab fa-youtube' },
]

function safeHttpUrl(value) {
    try {
        const parsed = new URL(value)
        return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : ''
    } catch {
        return ''
    }
}

function normalizeProjectLink(key, value) {
    const input = String(value || '').trim()
    if (!input) return ''
    if (/^https?:\/\//i.test(input)) return safeHttpUrl(input)
    if (/^[\w.-]+\.[a-z]{2,}(?:\/|$)/i.test(input)) return safeHttpUrl(`https://${input}`)

    const handle = input.replace(/^@/, '').replace(/^\/+|\/+$/g, '')
    if (!handle || /[:?#]/.test(handle)) return ''
    if (key === 'twitter') return `https://x.com/${handle}`
    if (key === 'telegram') return `https://t.me/${handle}`
    if (key === 'youtube') return `https://www.youtube.com/@${handle}`
    return key === 'website' ? safeHttpUrl(`https://${handle}`) : ''
}

function errorKey(error) {
    const message = error?.error?.message || error?.data?.message || error?.reason || error?.message || String(error)
    if (message === 'walletRequired') return 'walletRequired'
    if (message === 'networkRequired') return 'networkRequired'
    if (message === 'invalidAddress') return 'invalidAddress'
    return message
}

function StatusBadge({ market, t }) {
    if (market.core?.retired) return <span className="market-status is-danger"><i className="fa fa-archive" /> {t('retired')}</span>
    if (market.core?.migrationActive) return <span className="market-status is-warning"><i className="fa fa-truck-loading" /> {t('migrationActive')}</span>
    if (market.core?.paused) return <span className="market-status is-warning"><i className="fa fa-pause" /> {t('paused')}</span>
    return <span className="market-status is-live"><i className="fa fa-circle" /> {t('trading')}</span>
}

export function MarketOverview({ market, t, onList }) {
    const { core, project } = market
    const currency = core.currency
    const logo = project.logo || project.image || ''
    const description = project.description || `${core.nftName} (${core.nftSymbol})`
    const descriptionRef = useRef(null)
    const [descriptionExpanded, setDescriptionExpanded] = useState(false)
    const [descriptionOverflow, setDescriptionOverflow] = useState(false)
    const descriptionHasLines = description.includes('\n')
    const projectLinks = PROJECT_LINKS
        .map((item) => ({ ...item, href: normalizeProjectLink(item.key, project[item.key]) }))
        .filter((item) => item.href)

    useEffect(() => {
        setDescriptionExpanded(false)
    }, [description])

    useEffect(() => {
        const element = descriptionRef.current
        if (!element || descriptionExpanded) return undefined
        const measure = () => setDescriptionOverflow(element.scrollHeight > element.clientHeight + 1)
        measure()
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure)
            return () => window.removeEventListener('resize', measure)
        }
        const observer = new ResizeObserver(measure)
        observer.observe(element)
        return () => observer.disconnect()
    }, [description, descriptionExpanded])

    const canToggleDescription = descriptionHasLines || descriptionOverflow

    return <section className="market-overview">
        <div className="market-overview__identity">
            <ProjectLogo className="market-overview__logo" logo={logo} symbol={core.nftSymbol} loading={market.projectLoading} loadingLabel={t('loadingProjectLogo')} />
            <div>
                <div className="market-overview__title-row">
                    <h1>{project.name || core.nftName}</h1>
                    <StatusBadge market={market} t={t} />
                    {core.official && <span className="market-official-badge"><i className="fa fa-check-circle" /> {t('officialMarket')}</span>}
                </div>
                <p
                    ref={descriptionRef}
                    className={`market-overview__description ${descriptionExpanded ? 'is-expanded' : ''} ${canToggleDescription ? 'has-toggle' : ''}`}
                >
                    {description}
                </p>
                {canToggleDescription && <button
                    type="button"
                    className="market-overview__description-toggle"
                    aria-label={t(descriptionExpanded ? 'collapseDescription' : 'expandDescription')}
                    onClick={() => setDescriptionExpanded((current) => !current)}
                >
                    {t(descriptionExpanded ? 'collapseDescription' : 'expandDescription')}
                    <i className={`fa fa-chevron-${descriptionExpanded ? 'up' : 'down'}`} />
                </button>}
                <div className="market-overview__action-row">
                    {projectLinks.length > 0 && <div className="market-overview__socials">
                        {projectLinks.map((item) => <a
                            key={item.key}
                            href={item.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            title={t(item.key)}
                            aria-label={t(item.key)}
                        >
                            <i className={item.icon} />
                        </a>)}
                    </div>}
                    <div className="market-overview__links">
                        <button type="button" onClick={() => market.copyValue(window.location.href)} title={t('share')}><i className="fa fa-share-alt" /></button>
                        <button type="button" className="market-overview__list" onClick={onList}><i className="fa fa-plus" /> {t('listYourNft')}</button>
                    </div>
                </div>
                <div className="market-overview__addresses">
                    <span>{t('nftAddress')} <AddressValue value={core.nftAddress} copyValue={market.copyValue} label={t('copy')} /></span>
                    <span className="market-overview__currency"><i className="fa fa-coins" /> {currency.symbol}</span>
                    <span>{t('marketAddress')} <AddressValue value={core.marketAddress} copyValue={market.copyValue} label={t('copy')} /></span>
                </div>
            </div>
        </div>
        <div className="market-overview__metrics">
            <div title={t('directStatsHint')}><small>{t('floor')}</small><strong>{core.floorPrice && !core.floorPrice.isZero() ? `${formatAmount(core.floorPrice, currency.decimals)} ${currency.symbol}` : '—'}</strong></div>
            <div title={core.volume24h ? t('directStatsHint') : t('volume24hUnavailable')}><small>{t('volume24h')}</small><strong>{core.volume24h ? `${formatAmount(core.volume24h, currency.decimals)} ${currency.symbol}` : '—'}</strong></div>
            <div><small>{t('volume')}</small><strong>{formatAmount(core.totalVolume, currency.decimals)} {currency.symbol}</strong></div>
            <div><small>{t('collection')}</small><strong>{core.activeCount} {t('items')}</strong></div>
        </div>
    </section>
}

function MarketToolbar({ filters, setFilters, sort, setSort, t }) {
    const update = (key, value) => setFilters((current) => ({ ...current, [key]: value }))
    return <div className="market-toolbar">
        <label className="market-search"><i className="fa fa-search" /><input value={filters.search} onChange={(event) => update('search', event.target.value)} placeholder={t('search')} />{filters.search && <button type="button" onClick={() => update('search', '')}><i className="fa fa-times" /></button>}</label>
        <MarketSelect className="is-status" label={t('status')} value={filters.status} onChange={(value) => update('status', value)} options={[
            { value: 'all', label: t('allStatus') },
            { value: 'active', label: t('active') },
            { value: 'expired', label: t('expired') },
        ]} />
        <label className="market-price-filter"><span>{t('price')}</span><input type="number" min="0" step="any" value={filters.minPrice} onChange={(event) => update('minPrice', event.target.value)} placeholder={t('minimum')} /><i className="fa fa-minus" /><input type="number" min="0" step="any" value={filters.maxPrice} onChange={(event) => update('maxPrice', event.target.value)} placeholder={t('maximum')} /></label>
        <MarketSelect className="is-expiry" label={t('expires')} value={filters.expiry} onChange={(value) => update('expiry', value)} options={[
            { value: 'all', label: t('allExpiry') },
            { value: '24h', label: t('within24h') },
            { value: '7d', label: t('within7d') },
            { value: 'never', label: t('permanent') },
        ]} />
        <MarketSelect label={t('sort')} value={sort} onChange={setSort} icon="fa-sort-amount-down" options={[
            { value: 'newest', label: t('newest') },
            { value: 'low', label: t('priceLow') },
            { value: 'high', label: t('priceHigh') },
        ]} />
    </div>
}

export function MarketTabs({ market, tab, setTab, t }) {
    const tabs = [
        ['market', 'fa-store'],
        ['myNfts', 'fa-images'],
        ['myListings', 'fa-tags'],
        ['transactions', 'fa-exchange-alt'],
        ['activity', 'fa-stream'],
        ...(canAccessMarketAdmin(market) ? [['admin', 'fa-user-shield']] : []),
    ]

    return <nav className="market-tabs">{tabs.map(([key, icon]) => <button
        type="button"
        key={key}
        className={tab === key ? 'is-active' : ''}
        onClick={() => setTab(key)}
    >
        <i className={`fa ${icon}`} />
        <span>{t(key)}</span>
        {key === 'market' && <small>{market.core.activeCount}</small>}
        {key === 'myNfts' && market.wallet.account && <small>{market.owned.total}</small>}
    </button>)}</nav>
}

function ListingGrid({ market, items, metadata, t, filters, sort, onOpen, selectedTokenId, language }) {
    const sorted = useMemo(() => [...items]
        .filter((item) => {
            const searchMatches = !filters.search || item.tokenId.toString().includes(filters.search.trim())
            const numericPrice = Number(formatAmount(item.price, market.core.currency.decimals, 12))
            const minMatches = !filters.minPrice || numericPrice >= Number(filters.minPrice)
            const maxMatches = !filters.maxPrice || numericPrice <= Number(filters.maxPrice)
            const now = Math.floor(Date.now() / 1000)
            const expiry = Number(item.expiresAt || 0)
            const active = Boolean(item.active) && (!expiry || expiry > now)
            const statusMatches = filters.status === 'all' || (filters.status === 'active' ? active : !active)
            const expiryMatches = filters.expiry === 'all'
                || (filters.expiry === 'never' ? expiry === 0 : expiry > now && expiry <= now + (filters.expiry === '24h' ? 86400 : 604800))
            return searchMatches && minMatches && maxMatches && statusMatches && expiryMatches
        })
        .sort((a, b) => {
            if (sort === 'low') return a.price.eq(b.price) ? 0 : a.price.lt(b.price) ? -1 : 1
            if (sort === 'high') return a.price.eq(b.price) ? 0 : a.price.gt(b.price) ? -1 : 1
            return a.listingId.eq(b.listingId) ? 0 : a.listingId.gt(b.listingId) ? -1 : 1
        }), [filters, items, market.core.currency.decimals, sort])

    if (market.listings.loading) return <div className="market-grid-skeleton">{Array.from({ length: 10 }, (_, index) => <span key={index} />)}</div>
    if (!sorted.length) return <EmptyState title={t('noListings')} />
    return <div className="market-nft-grid">{sorted.map((listing) => {
        const id = listing.tokenId.toString()
        return <NftCard key={id} tokenId={id} metadata={metadata[id]} price={formatAmount(listing.price, market.core.currency.decimals)} symbol={market.core.currency.symbol} seller={listing.seller} expiresAt={listing.expiresAt} language={language} sellerLabel={t('seller')} remainingLabel={t('remaining')} selected={selectedTokenId === id} listingCard onOpen={() => onOpen(listing, metadata[id])} />
    })}</div>
}

function MarketTab({ market, t, filters, setFilters, sort, setSort, onOpen, selectedTokenId, language }) {
    return <>
        <MarketToolbar filters={filters} setFilters={setFilters} sort={sort} setSort={setSort} t={t} />
        <ListingGrid market={market} items={market.listings.items} metadata={market.listings.metadata} t={t} filters={filters} sort={sort} onOpen={onOpen} selectedTokenId={selectedTokenId} language={language} />
        <Pagination page={market.listings.page} total={market.listings.total} pageSize={PAGE_SIZE} onPage={market.loadListings} t={t} />
        <div className="market-grid-total">{t('totalItems', { count: market.core.activeCount })}</div>
    </>
}

function OwnedTab({ market, t, selected, toggleSelected, setSelected, onList, onOpen }) {
    const pageIds = market.owned.items.map((value) => value.toString())
    if (!market.wallet.account) return <EmptyState icon="fa-wallet" title={t('walletRequired')} />
    if (market.owned.loading) return <div className="market-grid-skeleton">{Array.from({ length: 10 }, (_, index) => <span key={index} />)}</div>
    if (!market.owned.items.length) return <EmptyState icon="fa-images" title={t('noWalletNfts')} />
    return <>
        <div className="market-selection-bar">
            <div><strong>{t('selected', { count: selected.length })}</strong><small>{t('batchLimit')}</small></div>
            <div><button type="button" onClick={() => setSelected(Array.from(new Set([...selected, ...pageIds])))}><i className="fa fa-check-double" /> {t('selectAllPage')}</button>{selected.length > 0 && <button type="button" onClick={() => setSelected([])}><i className="fa fa-times" /> {t('clear')}</button>}<button type="button" className="is-primary" disabled={!selected.length} onClick={() => onList(selected)}><i className="fa fa-layer-group" /> {selected.length > 1 ? t('batchList') : t('list')}</button></div>
        </div>
        <div className="market-nft-grid market-owned-grid">{market.owned.items.map((tokenId) => {
            const id = tokenId.toString()
            return <NftCard key={id} tokenId={id} metadata={market.owned.metadata[id]} selected={selected.includes(id)} onSelect={toggleSelected} onOpen={() => onOpen(id, market.owned.metadata[id])} actionLabel={t('list')} onAction={() => onList([id])} />
        })}</div>
        <Pagination page={market.owned.page} total={market.owned.total} pageSize={PAGE_SIZE} onPage={market.loadOwned} t={t} />
    </>
}

export function NftSelectionDialog({ market, selected, setSelected, onClose, onNext, t }) {
    const [search, setSearch] = useState('')
    const query = search.trim().toLowerCase()
    const visibleItems = market.owned.items.filter((tokenId) => {
        if (!query) return true
        const id = tokenId.toString()
        const name = market.owned.metadata[id]?.metadata?.name || ''
        return id.toLowerCase().includes(query) || name.toLowerCase().includes(query)
    })
    const visibleIds = visibleItems.map((tokenId) => tokenId.toString())
    const toggle = (id) => setSelected((current) => current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id])
    const selectVisible = () => setSelected((current) => Array.from(new Set([...current, ...visibleIds])))
    const footer = <>
        <strong className="market-nft-picker__count">{t('selected', { count: selected.length })}</strong>
        <button type="button" className="market-button" onClick={onClose}>{t('cancel')}</button>
        <button type="button" className="market-button is-primary" disabled={!selected.length} onClick={() => onNext(selected)}>
            {t('nextSetPrice')} <i className="fa fa-arrow-right" />
        </button>
    </>

    return <MarketDialog title={t('selectNftsTitle')} icon="fa-images" onClose={onClose} footer={footer} wide>
        <div className="market-nft-picker">
            {!market.wallet.account ? <EmptyState icon="fa-wallet" title={t('walletRequired')} /> : <>
                <div className="market-nft-picker__toolbar">
                    <label className="market-search"><i className="fa fa-search" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('search')} /></label>
                    <button type="button" onClick={selectVisible} disabled={!visibleIds.length}><i className="fa fa-check-double" /> {t('selectAllPage')}</button>
                    {selected.length > 0 && <button type="button" onClick={() => setSelected([])}><i className="fa fa-times" /> {t('clear')}</button>}
                </div>
                {market.owned.loading ? <div className="market-grid-skeleton">{Array.from({ length: 8 }, (_, index) => <span key={index} />)}</div>
                    : visibleItems.length ? <div className="market-nft-grid market-nft-picker__grid">{visibleItems.map((tokenId) => {
                        const id = tokenId.toString()
                        return <NftCard
                            key={id}
                            tokenId={id}
                            metadata={market.owned.metadata[id]}
                            selected={selected.includes(id)}
                            onSelect={toggle}
                            selectionMode
                        />
                    })}</div>
                        : <EmptyState icon="fa-images" title={t('noWalletNfts')} />}
                <Pagination page={market.owned.page} total={market.owned.total} pageSize={PAGE_SIZE} onPage={market.loadOwned} t={t} />
            </>}
        </div>
    </MarketDialog>
}

function OwnedNftDetailDialog({ market, detail, onClose, onList, t }) {
    const id = detail.tokenId
    const name = detail.metadata?.metadata?.name || `NFT #${id}`
    const description = detail.metadata?.metadata?.description || ''

    return <MarketDialog
        title={name}
        eyebrow={`${t('tokenId')} #${id}`}
        icon="fa-gem"
        onClose={onClose}
        wide
        footer={<>
            <button type="button" className="market-button" onClick={onClose}>{t('close')}</button>
            <button type="button" className="market-button is-primary" onClick={() => onList([id])}><i className="fa fa-tag" /> {t('list')}</button>
        </>}
    >
        <div className="market-owned-detail">
            <NftMedia entry={detail.metadata} alt={name} />
            <div className="market-owned-detail__content">
                <div className="market-owned-detail__identity">
                    <small>{t('tokenId')}</small>
                    <strong>#{id}</strong>
                </div>
                <NftDescription description={description} t={t} />
                <div className="market-detail__info">
                    <div><span>{t('contract')}</span><AddressValue value={market.core.nftAddress} copyValue={market.copyValue} label={t('copy')} /></div>
                    <div><span>{t('project')}</span><strong>{market.project.name || market.core.nftName}</strong></div>
                    <div><span>{t('standard')}</span><strong>ERC-721</strong></div>
                    <div><span>{t('network')}</span><strong>{market.network.name}</strong></div>
                </div>
            </div>
        </div>
    </MarketDialog>
}

function HistoryTab({ page, kind, market, t, language, onOpen }) {
    const loadHistoryPage = market.loadHistoryPage
    useEffect(() => { loadHistoryPage(kind, 1) }, [kind, loadHistoryPage])
    if (!market.wallet.account && kind !== 'activity') return <EmptyState icon="fa-wallet" title={t('walletRequired')} />
    if (page.loading) return <div className="market-table-loading"><i className="fa fa-spinner fa-spin" /></div>
    const activityNotice = kind === 'activity' && <div className="market-history-note">
        <i className="fa fa-info-circle" />
        <span><strong>{t('activityScopeTitle')}</strong>{t('activityScopeHint')}</span>
    </div>
    if (!page.items.length) return <>{activityNotice}<EmptyState icon="fa-receipt" title={t('noHistory')} /></>
    return <>
        {activityNotice}
        <div className="market-history-list">{page.items.map((item) => {
            const id = item.tokenId.toString()
            const isListing = Boolean(item.listingId && item.expiresAt !== undefined)
            return <article key={`${kind}-${item.listingId?.toString()}-${item.saleId?.toString() || ''}`} onDoubleClick={() => isListing && onOpen(item, page.metadata[id])}>
                <NftMedia entry={page.metadata[id]} alt={`NFT #${id}`} />
                <div><small>NFT #{id}</small><strong>{page.metadata[id]?.metadata?.name || `NFT #${id}`}</strong><span>{isListing ? `${t('seller')}: ${shortAddress(item.seller)}` : `${t('buyer')}: ${shortAddress(item.buyer)}`}</span></div>
                <div><small>{isListing ? t('price') : t('total')}</small><strong>{formatAmount(item.price, market.core.currency.decimals)} {market.core.currency.symbol}</strong><span>{isListing ? formatDate(item.expiresAt, language) : formatDate(item.soldAt, language)}</span></div>
                {isListing && item.active && item.seller.toLowerCase() === market.wallet.account.toLowerCase() && <button type="button" onClick={() => onOpen(item, page.metadata[id])}><i className="fa fa-ellipsis-h" /></button>}
            </article>
        })}</div>
        <Pagination page={page.page} total={page.total} pageSize={PAGE_SIZE} onPage={(next) => market.loadHistoryPage(kind, next)} t={t} />
    </>
}

function TransactionsTab({ market, t, language, onOpen }) {
    const [kind, setKind] = useState('purchases')
    const page = kind === 'purchases' ? market.purchases : market.sales
    return <>
        <div className="market-history-switch">
            <button type="button" className={kind === 'purchases' ? 'is-active' : ''} onClick={() => setKind('purchases')}>{t('purchases')}</button>
            <button type="button" className={kind === 'sales' ? 'is-active' : ''} onClick={() => setKind('sales')}>{t('sales')}</button>
        </div>
        <HistoryTab page={page} kind={kind} market={market} t={t} language={language} onOpen={onOpen} />
    </>
}

export function LegacyAdminTab({ market, t }) {
    const { core } = market
    const [settlement, setSettlement] = useState(core.settlementAddress)
    const [projectFee, setProjectFee] = useState(String(core.projectFeeBps / 100))
    const [recipient, setRecipient] = useState(core.projectFeeRecipient)
    const [projectInfo, setProjectInfo] = useState({
        name: market.project.name || core.nftName,
        logo: market.project.logo || market.project.image || '',
        description: market.project.description || '',
        website: market.project.website || '',
        twitter: market.project.twitter || '',
        telegram: market.project.telegram || '',
        youtube: market.project.youtube || '',
    })
    const [royaltyMode, setRoyaltyMode] = useState(String(core.royaltyMode))
    const [royaltyFee, setRoyaltyFee] = useState(String(core.customRoyaltyBps / 100))
    const [royaltyRecipient, setRoyaltyRecipient] = useState(core.customRoyaltyRecipient)
    const [newOwner, setNewOwner] = useState('')
    const [migrationTarget, setMigrationTarget] = useState(core.migrationTarget === ZERO_ADDRESS ? '' : core.migrationTarget)
    const [busy, setBusy] = useState('')
    const isOwner = market.wallet.account && market.wallet.account.toLowerCase() === core.owner.toLowerCase()
    const isPendingOwner = market.wallet.account && market.wallet.account.toLowerCase() === core.pendingOwner.toLowerCase()

    const execute = async (key, action) => {
        setBusy(key)
        try { await action() } catch (error) { market.showToast(errorKey(error), 'error') } finally { setBusy('') }
    }
    if (!isOwner && isPendingOwner) return <div className="market-admin"><div className="market-admin__notice"><i className="fa fa-key" /><span><strong>{t('pendingOwnership')}</strong><AddressValue value={core.marketAddress} copyValue={market.copyValue} label={t('copy')} /></span><button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('acceptOwner', () => market.runAdmin('acceptOwnership', []))}>{busy === 'acceptOwner' && <i className="fa fa-spinner fa-spin" />} {t('acceptOwnership')}</button></div></div>
    if (!isOwner) return <EmptyState icon="fa-user-shield" title={t('ownerOnly')} text={shortAddress(core.owner)} />
    return <div className="market-admin">
        <div className="market-admin__notice"><i className="fa fa-info-circle" /><span><strong>{t('adminTitle')}</strong>{t('adminHint')}</span></div>
        <section><header><i className="fa fa-coins" /><div><h3>{t('defaultSettlement')}</h3><p>{core.currency.name} ({core.currency.symbol})</p></div></header><label><span>{t('tokenContract')}</span><div className="market-input-action"><input value={settlement} onChange={(event) => setSettlement(event.target.value)} placeholder={ZERO_ADDRESS} /><button type="button" onClick={() => setSettlement(ZERO_ADDRESS)}>{t('nativeCurrency')}</button></div></label><button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('settlement', () => market.saveSettlement(settlement))}>{busy === 'settlement' && <i className="fa fa-spinner fa-spin" />} {t('saveSettlement')}</button></section>
        <section className="market-admin__project"><header><i className="fa fa-sliders-h" /><div><h3>{t('projectSettings')}</h3><p>{t('snapshotHint')}</p></div></header><div className="market-form-grid"><label><span>{t('feePercent')}</span><input type="number" min="0" max="20" step="0.01" value={projectFee} onChange={(event) => setProjectFee(event.target.value)} /></label><label><span>{t('feeRecipient')}</span><input value={recipient} onChange={(event) => setRecipient(event.target.value)} /></label></div><div className="market-form-grid"><label><span>{t('projectName')}</span><input value={projectInfo.name} onChange={(event) => setProjectInfo((current) => ({ ...current, name: event.target.value }))} /></label><label><span>{t('projectLogo')}</span><input value={projectInfo.logo} onChange={(event) => setProjectInfo((current) => ({ ...current, logo: event.target.value }))} /></label></div><label><span>{t('projectDescription')}</span><textarea value={projectInfo.description} onChange={(event) => setProjectInfo((current) => ({ ...current, description: event.target.value }))} /></label><div className="market-form-grid is-links">{['website', 'twitter', 'telegram', 'youtube'].map((key) => <label key={key}><span>{t(key)}</span><input value={projectInfo[key]} onChange={(event) => setProjectInfo((current) => ({ ...current, [key]: event.target.value }))} /></label>)}</div><div className="market-admin__actions"><button type="button" className="market-button" disabled={busy} onClick={() => execute('metadata', async () => { const metadataURI = await uploadMarketplaceMetadata({ ...market.project, ...projectInfo, nft: core.nftAddress, schemaVersion: 1 }); return market.runAdmin('setMetadataURI', [metadataURI]) })}>{busy === 'metadata' && <i className="fa fa-spinner fa-spin" />} {t('projectMetadata')}</button><button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('project', () => market.saveProjectFee(projectFee, recipient))}>{t('saveProject')}</button></div></section>
        <section><header><i className="fa fa-certificate" /><div><h3>{t('royaltySettings')}</h3><p>{t('snapshotHint')}</p></div></header><div className="market-segments"><button type="button" className={royaltyMode === '0' ? 'is-active' : ''} onClick={() => setRoyaltyMode('0')}>{t('royaltyNone')}</button><button type="button" className={royaltyMode === '1' ? 'is-active' : ''} onClick={() => setRoyaltyMode('1')}>{t('royaltyErc2981')}</button><button type="button" className={royaltyMode === '2' ? 'is-active' : ''} onClick={() => setRoyaltyMode('2')}>{t('royaltyCustom')}</button></div>{royaltyMode === '2' && <div className="market-form-grid"><label><span>{t('royaltyPercent')}</span><input type="number" min="0" max="15" step="0.01" value={royaltyFee} onChange={(event) => setRoyaltyFee(event.target.value)} /></label><label><span>{t('royaltyRecipient')}</span><input value={royaltyRecipient} onChange={(event) => setRoyaltyRecipient(event.target.value)} /></label></div>}<button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('royalty', () => market.saveRoyalty(royaltyMode, royaltyFee, royaltyRecipient))}>{t('saveRoyalty')}</button></section>
        <section><header><i className="fa fa-shield-alt" /><div><h3>{t('marketControls')}</h3><p>{core.paused ? t('paused') : t('trading')}</p></div></header><div className="market-admin__actions"><button type="button" className={`market-button ${core.paused ? 'is-primary' : 'is-danger'}`} disabled={busy || core.retired} onClick={() => execute('pause', () => market.runAdmin('setPaused', [!core.paused]))}><i className={`fa fa-${core.paused ? 'play' : 'pause'}`} /> {core.paused ? t('resumeMarket') : t('pauseMarket')}</button></div><div className="market-admin__divider" /><label><span>{t('newOwner')}</span><div className="market-input-action"><input value={newOwner} onChange={(event) => setNewOwner(event.target.value)} /><button type="button" disabled={busy || !newOwner} onClick={() => execute('owner', () => market.runAdmin('transferOwnership', [newOwner]))}>{t('transferOwner')}</button></div></label></section>
        <section><header><i className="fa fa-truck-loading" /><div><h3>{t('migration')}</h3><p>{core.migrationActive ? `${t('migrationActive')} · ${core.migrationCursor.toString()}` : t('adminHint')}</p></div></header><label><span>{t('migrationTarget')}</span><input value={migrationTarget} onChange={(event) => setMigrationTarget(event.target.value)} disabled={core.migrationActive} /></label><div className="market-admin__actions">{!core.migrationActive ? <button type="button" className="market-button is-primary" disabled={busy || !migrationTarget} onClick={() => execute('migration', () => market.runAdmin('beginMigration', [migrationTarget]))}>{t('beginMigration')}</button> : <><button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute('migrate', () => market.runAdmin('migrateBatch', [20]))}>{t('continueMigration')}</button>{!core.migrationStarted && <button type="button" className="market-button" disabled={busy} onClick={() => execute('cancelMigration', () => market.runAdmin('cancelMigration', []))}>{t('cancelMigration')}</button>}</>}</div></section>
    </div>
}

export function ListingDialog({ market, tokenIds, onClose, t }) {
    const [mode, setMode] = useState('uniform')
    const [price, setPrice] = useState('')
    const [prices, setPrices] = useState({})
    const [expiry, setExpiry] = useState('30d')
    const [busy, setBusy] = useState(false)
    const [progress, setProgress] = useState([])
    const [result, setResult] = useState(null)
    const multiple = tokenIds.length > 1
    const customRoyaltyBps = market.core.royaltyMode === 2 ? market.core.customRoyaltyBps : 0

    const fee = useMemo(() => {
        try {
            if (!price || Number(price) <= 0) return null
            return buildFeeBreakdown({ price: ethers.utils.parseUnits(price, market.core.currency.decimals), projectFeeBps: market.core.projectFeeBps, platformFeeBps: market.core.platformFeeBps, royaltyBps: customRoyaltyBps })
        } catch (error) { return null }
    }, [customRoyaltyBps, market.core, price])

    const submit = async () => {
        setBusy(true)
        setProgress([])
        try {
            const finalResult = await market.listTokens({ tokenIds, uniformPrice: price, priceByToken: mode === 'per-item' ? prices : null, expiryPreset: expiry, onProgress: (entry) => setProgress((current) => [...current, entry]) })
            const completed = finalResult.failed.length === 0
                && finalResult.successfulTokenIds.length === tokenIds.length
            if (completed) {
                market.setTransactionSuccess({
                    id: `listing-${Date.now()}`,
                    message: multiple ? 'batchListingSuccess' : 'listingSuccess',
                    successCount: finalResult.successfulTokenIds.length,
                    txHash: finalResult.transactionHashes?.[finalResult.transactionHashes.length - 1] || '',
                })
                onClose()
                return
            }
            setResult(finalResult)
        } catch (error) {
            market.showToast(errorKey(error), 'error')
        } finally { setBusy(false) }
    }

    const footer = result
        ? <button type="button" className="market-button is-primary" onClick={onClose}>{t('done')}</button>
        : <><button type="button" className="market-button" onClick={onClose} disabled={busy}>{t('cancel')}</button><button type="button" className="market-button is-primary" onClick={submit} disabled={busy || (mode === 'uniform' && (!price || Number(price) <= 0))}>{busy && <i className="fa fa-spinner fa-spin" />} {multiple ? t('confirmBatch') : t('confirmListing')}</button></>

    return <MarketDialog title={multiple ? t('batchTitle') : t('listTitle')} eyebrow={t('selected', { count: tokenIds.length })} icon="fa-tags" onClose={onClose} footer={footer} wide={multiple}>
        {result ? <div className="market-result"><i className="fa fa-check-circle" /><h3>{t('listingSuccess')}</h3><p>{t('listedCount', { success: result.successfulTokenIds.length })} · {t('failedCount', { failed: result.failed.length })}</p>{result.failed.map((item) => <div key={String(item.tokenId)}><strong>NFT #{String(item.tokenId)}</strong><span>{item.reason}</span></div>)}</div> : <>
            {multiple && <div className="market-batch-note"><i className="fa fa-layer-group" /><span>{t('batchLimit')}</span></div>}
            {multiple && <div className="market-segments"><button type="button" className={mode === 'uniform' ? 'is-active' : ''} onClick={() => setMode('uniform')}>{t('uniformPrice')}</button><button type="button" className={mode === 'per-item' ? 'is-active' : ''} onClick={() => setMode('per-item')}>{t('perItemPrice')}</button></div>}
            {mode === 'uniform' ? <label className="market-field"><span>{t('listingPrice')}</span><div className="market-price-input"><input type="number" min="0" step="any" value={price} onChange={(event) => setPrice(event.target.value)} autoFocus /><strong>{market.core.currency.symbol}</strong></div></label> : <div className="market-item-prices">{tokenIds.map((id) => <label key={id}><span>NFT #{id}</span><div className="market-price-input"><input type="number" min="0" step="any" value={prices[id] || ''} onChange={(event) => setPrices((current) => ({ ...current, [id]: event.target.value }))} /><strong>{market.core.currency.symbol}</strong></div></label>)}</div>}
            <div className="market-field"><span>{t('duration')}</span><div className="market-expiry">{[['1d', 'oneDay'], ['7d', 'sevenDays'], ['30d', 'thirtyDays'], ['90d', 'ninetyDays'], ['never', 'never']].map(([value, label]) => <button type="button" key={value} className={expiry === value ? 'is-active' : ''} onClick={() => setExpiry(value)}>{t(label)}</button>)}</div></div>
            <div className="market-fee-card"><header><span>{t('feeSnapshot')}</span><i className="fa fa-lock" /></header>{market.core.projectFeeBps > 0 && <div><span>{t('projectFee')}</span><strong>{market.core.projectFeeBps / 100}%</strong></div>}{market.core.platformFeeBps > 0 && <div><span>{t('platformFee')}</span><strong>{market.core.platformFeeBps / 100}%</strong></div>}{(market.core.royaltyMode === 1 || customRoyaltyBps > 0) && <div><span>{t('royalty')}</span><strong>{market.core.royaltyMode === 1 ? 'ERC-2981' : `${customRoyaltyBps / 100}%`}</strong></div>}{fee && <div className="is-total"><span>{t('sellerGets')}</span><strong>{formatAmount(fee.sellerProceeds, market.core.currency.decimals)} {market.core.currency.symbol}</strong></div>}<p>{t('snapshotHint')}</p></div>
            {progress.length > 0 && <div className="market-progress"><strong>{t('batchProgress')}</strong>{progress.slice(-5).map((entry, index) => <p key={`${entry.phase}-${index}`} className={entry.phase.includes('failed') ? 'is-error' : ''}><i className={`fa ${entry.phase.includes('success') ? 'fa-check-circle' : entry.phase.includes('failed') ? 'fa-exclamation-circle' : 'fa-circle-notch fa-spin'}`} /> {entry.phase === 'batch-failed' ? t('batchRetry') : entry.tokenId ? `NFT #${entry.tokenId}` : `${entry.size || ''}`}</p>)}</div>}
        </>}
    </MarketDialog>
}

function ListingDetail({ market, detail, onClose, t, language, onEdit, docked = false }) {
    const [busy, setBusy] = useState(false)
    const [closing, setClosing] = useState(false)
    const closeTimerRef = useRef(null)
    const listing = detail.listing
    const id = listing.tokenId.toString()
    const mine = market.wallet.account && listing.seller.toLowerCase() === market.wallet.account.toLowerCase()
    const requestClose = useCallback(() => {
        if (!docked) {
            onClose()
            return
        }
        if (closing) return
        setClosing(true)
        closeTimerRef.current = window.setTimeout(onClose, 180)
    }, [closing, docked, onClose])
    useEffect(() => () => {
        if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current)
    }, [])
    const execute = async (action) => { setBusy(true); try { await action(); requestClose() } catch (error) { market.showToast(errorKey(error), 'error') } finally { setBusy(false) } }
    const feeTotal = listing.projectFeeAmount.add(listing.platformFeeAmount).add(listing.royaltyAmount)
    const feeRows = buildVisibleFeeRows({
        projectFeeAmount: listing.projectFeeAmount,
        platformFeeAmount: listing.platformFeeAmount,
        royaltyAmount: listing.royaltyAmount,
        sellerProceeds: listing.price.sub(feeTotal),
    })
    const ownerActions = <>{mine && listing.active && <button type="button" className="market-button is-danger" disabled={busy} onClick={() => execute(() => market.cancelListing(listing))}><i className="fa fa-times-circle" /> {t('cancelListing')}</button>}{mine && listing.active && <button type="button" className="market-button" disabled={busy} onClick={() => onEdit(detail)}><i className="fa fa-pen" /> {t('updateListing')}</button>}</>
    const buyerAction = !mine && listing.active && <button type="button" className="market-button is-primary" disabled={busy} onClick={() => execute(() => market.buyListing(listing))}>{busy && <i className="fa fa-spinner fa-spin" />} {t('buy')}</button>
    const actions = mine ? ownerActions : buyerAction
    const content = <div className={`market-detail ${docked ? 'is-docked' : ''}`}>
        <NftMedia entry={detail.metadata} alt={`NFT #${id}`} />
        <div className="market-detail__content">
            <div className="market-detail__seller"><span>{t('seller')}</span><AddressValue value={listing.seller} copyValue={market.copyValue} label={t('copy')} /><span>{t('expires')} · {formatRemaining(listing.expiresAt, language)}</span></div>
            <div className="market-detail__price"><span><small>{t('currentPrice')}</small><h3>{formatAmount(listing.price, market.core.currency.decimals)} <small>{market.core.currency.symbol}</small></h3></span>{docked && buyerAction && <div className="market-detail__actions">{buyerAction}</div>}</div>
            <dl>{feeRows.map((row) => <div key={row.key} className={row.total ? 'is-total' : ''}><dt>{t(row.key)}</dt><dd>{formatAmount(row.amount, market.core.currency.decimals)} {market.core.currency.symbol}</dd></div>)}</dl>
            <NftDescription description={detail.metadata?.metadata?.description} t={t} />
            <div className="market-detail__info">
                <div><span>{t('contract')}</span><AddressValue value={market.core.nftAddress} copyValue={market.copyValue} label={t('copy')} /></div>
                <div><span>{t('project')}</span><strong>{market.project.name || market.core.nftName}</strong></div>
                <div><span>{t('tokenId')}</span><strong>#{id}</strong></div>
                <div><span>{t('standard')}</span><strong>ERC-721</strong></div>
                <div><span>{t('network')}</span><strong>{market.network.name}</strong></div>
                <div><span>{t('settlement')}</span><strong>{market.core.currency.symbol}</strong></div>
            </div>
            {docked && mine && listing.active && <div className="market-detail__owner-actions">{ownerActions}</div>}
        </div>
    </div>

    if (!docked) return <MarketDialog title={detail.metadata?.metadata?.name || `NFT #${id}`} eyebrow={`${t('tokenId')} #${id}`} icon="fa-gem" onClose={onClose} footer={actions} wide>{content}</MarketDialog>
    return <aside className={`market-detail-dock ${closing ? 'is-closing' : ''}`}>
        <header><div><small>#{id}</small><h2>{detail.metadata?.metadata?.name || `NFT #${id}`}</h2><p>{market.project.name || market.core.nftName} · <AddressValue value={market.core.nftAddress} copyValue={market.copyValue} label={t('copy')} /></p></div><span><button type="button" onClick={() => market.copyValue(window.location.href)} title={t('share')}><i className="fa fa-share-alt" /></button><button type="button" onClick={requestClose} title={t('close')}><i className="fa fa-times" /></button></span></header>
        <div className="market-detail-dock__body">{content}</div>
    </aside>
}

function EditListingDialog({ market, detail, onClose, t }) {
    const [price, setPrice] = useState(formatAmount(detail.listing.price, market.core.currency.decimals, 12))
    const [expiry, setExpiry] = useState('30d')
    const [busy, setBusy] = useState(false)
    const submit = async () => { setBusy(true); try { await market.updateListing(detail.listing, price, expiry); onClose() } catch (error) { market.showToast(errorKey(error), 'error') } finally { setBusy(false) } }
    return <MarketDialog title={t('updateListing')} eyebrow={`NFT #${detail.listing.tokenId.toString()}`} icon="fa-pen" onClose={onClose} footer={<><button type="button" className="market-button" onClick={onClose}>{t('cancel')}</button><button type="button" className="market-button is-primary" disabled={busy || !price} onClick={submit}>{busy && <i className="fa fa-spinner fa-spin" />} {t('confirm')}</button></>}><label className="market-field"><span>{t('listingPrice')}</span><div className="market-price-input"><input type="number" min="0" step="any" value={price} onChange={(event) => setPrice(event.target.value)} /><strong>{market.core.currency.symbol}</strong></div></label><div className="market-field"><span>{t('duration')}</span><div className="market-expiry">{[['1d', 'oneDay'], ['7d', 'sevenDays'], ['30d', 'thirtyDays'], ['90d', 'ninetyDays'], ['never', 'never']].map(([value, label]) => <button type="button" key={value} className={expiry === value ? 'is-active' : ''} onClick={() => setExpiry(value)}>{t(label)}</button>)}</div></div></MarketDialog>
}

export default function MarketplaceApp() {
    const { chain, marketAddress, tokenId } = useParams()
    const navigate = useNavigate()
    const { i18n } = useTranslation()
    const language = i18n.resolvedLanguage || 'en'
    const t = useCallback((key, values) => marketplaceText(language, key, values), [language])
    const market = useMarketplace({ chain, marketAddress })
    const [tab, setTab] = useState('market')
    const [filters, setFilters] = useState({ search: '', status: 'active', minPrice: '', maxPrice: '', expiry: 'all' })
    const [sort, setSort] = useState('low')
    const [selected, setSelected] = useState([])
    const [listingPickerOpen, setListingPickerOpen] = useState(false)
    const [listingIds, setListingIds] = useState(null)
    const [ownedDetail, setOwnedDetail] = useState(null)
    const [detail, setDetail] = useState(null)
    const [detailDismissed, setDetailDismissed] = useState(false)
    const [editing, setEditing] = useState(null)
    const [desktopDetail, setDesktopDetail] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1181px)').matches)
    const loadListingDetail = market.loadListingDetail
    const showToast = market.showToast
    const canAccessAdmin = canAccessMarketAdmin(market)

    const openDetail = useCallback((listing, metadata) => {
        setDetailDismissed(false)
        setDetail({ listing, metadata })
        navigate(`/${chain}/${marketAddress}/item/${listing.tokenId.toString()}${window.location.search}`, { replace: false })
    }, [chain, marketAddress, navigate])
    const closeDetail = useCallback(() => {
        setDetailDismissed(true)
        setDetail(null)
        navigate(`/${chain}/${marketAddress}${window.location.search}`, { replace: true })
    }, [chain, marketAddress, navigate])

    useEffect(() => {
        if (!tokenId || !market.core || (detail && detail.listing.tokenId.toString() === tokenId)) return
        loadListingDetail(tokenId).then(setDetail).catch((error) => showToast(errorKey(error), 'error'))
    }, [detail, loadListingDetail, market.core, showToast, tokenId])

    useEffect(() => {
        if (!desktopDetail || tab !== 'market' || tokenId || detail || detailDismissed || market.listings.loading || !market.listings.items.length) return
        const listing = market.listings.items[0]
        const id = listing.tokenId.toString()
        setDetail({ listing, metadata: market.listings.metadata[id] })
    }, [desktopDetail, detail, detailDismissed, market.listings.items, market.listings.loading, market.listings.metadata, tab, tokenId])

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
        const query = window.matchMedia('(min-width: 1181px)')
        const update = () => setDesktopDetail(query.matches)
        query.addEventListener?.('change', update)
        return () => query.removeEventListener?.('change', update)
    }, [])

    useEffect(() => {
        if (tab === 'admin' && !canAccessAdmin) setTab('market')
    }, [canAccessAdmin, tab])

    if (market.loading) return <div className="market-app"><div className="market-loader"><span><i className="fa fa-cube" /></span><h1>{t('loadingMarket')}</h1><p>{t('loadingHint')}</p><i className="fa fa-circle-notch fa-spin" /></div></div>
    if (market.error || !market.core) return <div className="market-app"><div className="market-loader is-error"><span><i className="fa fa-exclamation-triangle" /></span><h1>{t('loadFailed')}</h1><p>{market.error}</p><button type="button" className="market-button is-primary" onClick={market.reload}>{t('retry')}</button></div></div>

    const pageByTab = { myListings: [market.sellerListings, 'sellerListings'], activity: [market.activity, 'activity'] }

    return <div className="market-app">
        <MarketplaceHeader market={market} t={t} language={language} onLanguage={(next) => { i18n.changeLanguage(next); localStorage.setItem('i18nextLng', next) }} onConnect={market.connect} onDisconnect={market.disconnect} onSwitchNetwork={() => market.switchNetwork().catch((error) => market.showToast(errorKey(error), 'error'))} onAdmin={() => setTab('admin')} />
        <main className="market-shell">
            <MarketOverview market={market} t={t} onList={() => setListingPickerOpen(true)} />
            <div className={`market-content ${tab === 'market' && detail && desktopDetail ? 'has-detail' : ''}`}>
                <div className="market-content__main">
                    <MarketTabs market={market} tab={tab} setTab={setTab} t={t} />
                    <section className="market-workspace">
                        <div className="market-workspace__main">
                            {tab === 'market' && <MarketTab market={market} t={t} filters={filters} setFilters={setFilters} sort={sort} setSort={setSort} onOpen={openDetail} selectedTokenId={detail?.listing?.tokenId?.toString()} language={language} />}
                            {tab === 'myNfts' && <OwnedTab market={market} t={t} selected={selected} toggleSelected={(id) => setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])} setSelected={setSelected} onList={setListingIds} onOpen={(id, metadata) => setOwnedDetail({ tokenId: id, metadata })} />}
                            {pageByTab[tab] && <HistoryTab page={pageByTab[tab][0]} kind={pageByTab[tab][1]} market={market} t={t} language={language} onOpen={openDetail} />}
                            {tab === 'transactions' && <TransactionsTab market={market} t={t} language={language} onOpen={openDetail} />}
                            {tab === 'admin' && <AdminTab market={market} t={t} />}
                        </div>
                    </section>
                </div>
                {tab === 'market' && detail && desktopDetail && <ListingDetail market={market} detail={detail} onClose={closeDetail} t={t} language={language} onEdit={(value) => setEditing(value)} docked />}
            </div>
        </main>
        <footer className="market-footer"><span>© 2026 FatSale</span><span>{market.core.nftName} · {market.network.name}</span></footer>
        {listingPickerOpen && <NftSelectionDialog
            market={market}
            selected={selected}
            setSelected={setSelected}
            onClose={() => setListingPickerOpen(false)}
            onNext={(ids) => {
                setListingPickerOpen(false)
                setListingIds(ids)
            }}
            t={t}
        />}
        {listingIds && <ListingDialog market={market} tokenIds={listingIds} onClose={() => { setListingIds(null); setSelected([]) }} t={t} />}
        {ownedDetail && <OwnedNftDetailDialog
            market={market}
            detail={ownedDetail}
            onClose={() => setOwnedDetail(null)}
            onList={(ids) => {
                setOwnedDetail(null)
                setListingIds(ids)
            }}
            t={t}
        />}
        {tab === 'market' && detail && !editing && !desktopDetail && <ListingDetail market={market} detail={detail} onClose={closeDetail} t={t} language={language} onEdit={(value) => setEditing(value)} />}
        {editing && <EditListingDialog market={market} detail={editing} onClose={() => { setEditing(null); closeDetail() }} t={t} />}
        <TransactionSuccessDialog
            result={market.transactionSuccess}
            t={t}
            explorer={market.explorer}
            copyValue={market.copyValue}
            onClose={() => market.setTransactionSuccess(null)}
        />
        <Toast toast={market.toast} t={t} onClose={() => market.setToast(null)} copyValue={market.copyValue} />
    </div>
}
